import type { Drawing, DrawingStyle, LineStyle } from './types';

type Projectors = {
  timeToX: (tSec: number) => number | null;
  priceToY: (price: number) => number | null;
  width: number;
  height: number;
  axisRightWidth?: number;
};

function applyLineStyle(ctx: CanvasRenderingContext2D, style: DrawingStyle) {
  const width = Number(style.lineWidth ?? 2);
  ctx.lineWidth = Number.isFinite(width) && width > 0 ? width : 2;
  const lineStyle: LineStyle = style.lineStyle ?? 'solid';
  if (lineStyle === 'dashed') ctx.setLineDash([8, 5]);
  else if (lineStyle === 'dotted') ctx.setLineDash([2, 5]);
  else ctx.setLineDash([]);
}

function drawHandle(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.beginPath();
  ctx.fillStyle = '#F59E0B';
  ctx.arc(x, y, 4, 0, Math.PI * 2);
  ctx.fill();
}

function drawSegment(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}

function formatVlineLabel(tSec: number): string {
  const ms = Number(tSec) * 1000;
  if (!Number.isFinite(ms)) return 'VLine';
  const dt = new Date(ms);
  if (Number.isNaN(dt.getTime())) return 'VLine';

  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  return `${month}/${day} ${hh}:${mm}`;
}

function getHLineBoxes(
  projectors: Projectors,
  y: number,
  labelWidth: number,
  priceWidth: number,
) {
  const padding = 8;
  const boxH = 22;
  const rightInset = 4;
  const axisWidth = Math.max(40, projectors.axisRightWidth ?? 40);
  const axisLeft = projectors.width - axisWidth;
  const priceBoxW = Math.max(priceWidth + padding * 2, axisWidth - 2);
  const priceBx = Math.max(axisLeft, projectors.width - priceBoxW - rightInset);
  const by = Math.max(4, Math.min(projectors.height - boxH - 4, y - boxH / 2));
  const hasLabel = labelWidth > 0;
  const labelBoxW = hasLabel ? labelWidth + padding * 2 : 0;
  const labelBx = hasLabel ? Math.max(4, priceBx - labelBoxW) : priceBx;
  return {
    by,
    boxH,
    hasLabel,
    label: { bx: labelBx, boxW: labelBoxW },
    price: { bx: priceBx, boxW: priceBoxW },
  };
}

function getLabelBoxForVLine(projectors: Projectors, x: number, textWidth: number) {
  const padding = 8;
  const boxW = textWidth + padding * 2;
  const boxH = 22;
  const margin = 8;
  const unclampedX = x - boxW / 2;
  const bx = Math.max(4, Math.min(projectors.width - boxW - 4, unclampedX));
  const by = margin;
  return { bx, by, boxW, boxH };
}

function drawRoundedLabelBox(
  ctx: CanvasRenderingContext2D,
  bx: number,
  by: number,
  boxW: number,
  boxH: number,
  bgColor: string,
  label: string,
) {
  function parseColorToRGB(color: string): { r: number; g: number; b: number } | null {
    const s = String(color || '').trim();
    // hex #RRGGBB or #RGB
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
    if (hex) {
      const v = hex[1];
      if (v.length === 3) {
        const r = parseInt(v[0] + v[0], 16);
        const g = parseInt(v[1] + v[1], 16);
        const b = parseInt(v[2] + v[2], 16);
        return { r, g, b };
      }
      const r = parseInt(v.slice(0, 2), 16);
      const g = parseInt(v.slice(2, 4), 16);
      const b = parseInt(v.slice(4, 6), 16);
      return { r, g, b };
    }
    // rgb(...) or rgba(...)
    const rgb = /^rgba?\(([^)]+)\)$/i.exec(s);
    if (rgb) {
      const parts = rgb[1].split(',').map((p) => p.trim());
      if (parts.length >= 3) {
        const r = Number(parts[0]);
        const g = Number(parts[1]);
        const b = Number(parts[2]);
        if ([r, g, b].every((v) => Number.isFinite(v))) return { r, g, b };
      }
    }
    return null;
  }

  function isLightColor(color: string): boolean {
    const rgb = parseColorToRGB(color);
    if (!rgb) return false;
    // convert sRGB to linear
    const srgbToLinear = (c: number) => {
      const v = c / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    const R = srgbToLinear(rgb.r);
    const G = srgbToLinear(rgb.g);
    const B = srgbToLinear(rgb.b);
    const lum = 0.2126 * R + 0.7152 * G + 0.0722 * B;
    // threshold: treat as light when luminance is above 0.6
    return lum > 0.6;
  }
  const radius = 6;
  ctx.fillStyle = bgColor;
  ctx.beginPath();
  ctx.moveTo(bx + radius, by);
  ctx.lineTo(bx + boxW - radius, by);
  ctx.quadraticCurveTo(bx + boxW, by, bx + boxW, by + radius);
  ctx.lineTo(bx + boxW, by + boxH - radius);
  ctx.quadraticCurveTo(bx + boxW, by + boxH, bx + boxW - radius, by + boxH);
  ctx.lineTo(bx + radius, by + boxH);
  ctx.quadraticCurveTo(bx, by + boxH, bx, by + boxH - radius);
  ctx.lineTo(bx, by + radius);
  ctx.quadraticCurveTo(bx, by, bx + radius, by);
  ctx.closePath();
  ctx.fill();

  const metrics = ctx.measureText(label);
  const ascent = Number((metrics as any).actualBoundingBoxAscent ?? 0);
  const descent = Number((metrics as any).actualBoundingBoxDescent ?? 0);
  const textX = bx + boxW / 2;
  const textColor = isLightColor(bgColor) ? '#111' : '#fff';
  if (ascent > 0 || descent > 0) {
    const textY = by + (boxH + ascent - descent) / 2;
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = textColor;
    ctx.fillText(label, textX, textY);
  } else {
    ctx.textBaseline = 'middle';
    ctx.fillStyle = textColor;
    ctx.fillText(label, textX, by + boxH / 2);
  }
}

export function rayEndX(x1: number, y1: number, x2: number, y2: number, width: number): { x: number; y: number } {
  if (Math.abs(x2 - x1) < 1e-6) {
    return { x: x2, y: y2 < y1 ? 0 : 1e6 };
  }
  const m = (y2 - y1) / (x2 - x1);
  const x = width;
  const y = y1 + m * (x - x1);
  return { x, y };
}

export function channelOffset(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
): { ox: number; oy: number } {
  const vx = x2 - x1;
  const vy = y2 - y1;
  const len = Math.hypot(vx, vy) || 1;
  const nx = -vy / len;
  const ny = vx / len;
  const wx = x3 - x1;
  const wy = y3 - y1;
  const d = wx * nx + wy * ny;
  return { ox: nx * d, oy: ny * d };
}

export function renderDrawings(
  ctx: CanvasRenderingContext2D,
  drawings: Drawing[],
  selectedId: string | null,
  projectors: Projectors,
) {
  ctx.clearRect(0, 0, projectors.width, projectors.height);

  for (const drawing of drawings) {
    const style = drawing.style || {};
    const isSelected = drawing.id === selectedId;
    ctx.save();
    ctx.strokeStyle = isSelected ? '#F59E0B' : (style.lineColor || '#3B82F6');
    ctx.globalAlpha = Number(style.opacity ?? 1);
    applyLineStyle(ctx, style);

    if (drawing.tool === 'vline') {
      const t = Number((drawing.anchors as any).t);
      const x = projectors.timeToX(t);
      if (x == null) {
        ctx.restore();
        continue;
      }
      drawSegment(ctx, x, 0, x, projectors.height);

      const label = String(style.label || formatVlineLabel(t));
      ctx.save();
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      const metrics = ctx.measureText(label);
      const { bx, by, boxW, boxH } = getLabelBoxForVLine(projectors, x, metrics.width);
      const bgColor = style.lineColor || String(ctx.strokeStyle || '#8B5CF6');
      drawRoundedLabelBox(ctx, bx, by, boxW, boxH, bgColor, label);
      ctx.restore();

      if (isSelected) drawHandle(ctx, x, 14);
      ctx.restore();
      continue;
    }

    if (drawing.tool === 'hline') {
      const p = Number((drawing.anchors as any).p);
      const y = projectors.priceToY(p);
      if (y == null) {
        ctx.restore();
        continue;
      }
      drawSegment(ctx, 0, y, projectors.width, y);

      // label for horizontal line
      const label = String(style.label || '').trim();
      const priceLabel = p.toFixed(2);
      ctx.save();
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      const labelMetrics = label ? ctx.measureText(label) : { width: 0 };
      const priceMetrics = ctx.measureText(priceLabel);
      const { by, boxH, hasLabel, label: labelBox, price: priceBox } = getHLineBoxes(projectors, y, labelMetrics.width, priceMetrics.width);
      const bgColor = style.lineColor || String(ctx.strokeStyle || '#F59E0B');
      if (hasLabel) drawRoundedLabelBox(ctx, labelBox.bx, by, labelBox.boxW, boxH, bgColor, label);
      drawRoundedLabelBox(ctx, priceBox.bx, by, priceBox.boxW, boxH, bgColor, priceLabel);
      ctx.restore();

      if (isSelected) drawHandle(ctx, 16, y);
      ctx.restore();
      continue;
    }

    if (drawing.tool === 'note') {
      ctx.restore();
      continue;
    }

    const t1 = Number((drawing.anchors as any).t1);
    const p1 = Number((drawing.anchors as any).p1);
    const t2 = Number((drawing.anchors as any).t2);
    const p2 = Number((drawing.anchors as any).p2);
    const x1 = projectors.timeToX(t1);
    const y1 = projectors.priceToY(p1);
    const x2 = projectors.timeToX(t2);
    const y2 = projectors.priceToY(p2);

    if (x1 == null || y1 == null || x2 == null || y2 == null) {
      ctx.restore();
      continue;
    }

    if (drawing.tool === 'rect') {
      const left = Math.min(x1, x2);
      const right = Math.max(x1, x2);
      const top = Math.min(y1, y2);
      const bottom = Math.max(y1, y2);
      const fill = style.fillColor || 'rgba(59,130,246,0.18)';
      ctx.fillStyle = fill;
      ctx.fillRect(left, top, right - left, bottom - top);
      ctx.strokeRect(left, top, right - left, bottom - top);
      if (isSelected) {
        drawHandle(ctx, x1, y1);
        drawHandle(ctx, x2, y2);
      }
      ctx.restore();
      continue;
    }

    if (drawing.tool === 'trend') {
      let sx1 = x1;
      let sy1 = y1;
      let sx2 = x2;
      let sy2 = y2;
      if (style.extendLeft) {
        const leftRay = rayEndX(x2, y2, x1, y1, 0);
        sx1 = 0;
        sy1 = leftRay.y;
      }
      if (style.extendRight) {
        const rightRay = rayEndX(x1, y1, x2, y2, projectors.width);
        sx2 = projectors.width;
        sy2 = rightRay.y;
      }
      drawSegment(ctx, sx1, sy1, sx2, sy2);
      if (isSelected) {
        drawHandle(ctx, x1, y1);
        drawHandle(ctx, x2, y2);
      }
      ctx.restore();
      continue;
    }

    if (drawing.tool === 'ray') {
      const end = rayEndX(x1, y1, x2, y2, projectors.width);
      drawSegment(ctx, x1, y1, end.x, end.y);
      if (isSelected) {
        drawHandle(ctx, x1, y1);
        drawHandle(ctx, x2, y2);
      }
      ctx.restore();
      continue;
    }

    if (drawing.tool === 'channel') {
      const t3 = Number((drawing.anchors as any).t3);
      const p3 = Number((drawing.anchors as any).p3);
      const x3 = projectors.timeToX(t3);
      const y3 = projectors.priceToY(p3);
      if (x3 == null || y3 == null) {
        ctx.restore();
        continue;
      }
      const { ox, oy } = channelOffset(x1, y1, x2, y2, x3, y3);
      drawSegment(ctx, x1, y1, x2, y2);
      drawSegment(ctx, x1 + ox, y1 + oy, x2 + ox, y2 + oy);

      const fill = style.fillColor || 'rgba(59,130,246,0.12)';
      ctx.fillStyle = fill;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.lineTo(x2 + ox, y2 + oy);
      ctx.lineTo(x1 + ox, y1 + oy);
      ctx.closePath();
      ctx.fill();

      if (isSelected) {
        drawHandle(ctx, x1, y1);
        drawHandle(ctx, x2, y2);
        drawHandle(ctx, x3, y3);
      }
      ctx.restore();
      continue;
    }

    ctx.restore();
  }
}
